import React, { useState, useEffect } from 'react';
import { BomberosHeader } from '../../components/BomberosHeader';
import { useNavigate } from 'react-router-dom';
import { bomberosSolicitudesBajaAPI } from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
    ArrowLeft,
    FileCheck,
    CheckCircle,
    XCircle,
    Clock,
    FileText,
    User,
    Calendar,
    AlertTriangle,
    ExternalLink,
    Loader2,
    HardHat,
    Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const motivoLabels = {
    desgaste_normal: 'Desgaste por uso normal',
    daño_operacional: 'Daño en operación',
    vencimiento: 'Vencimiento de vida útil',
    extravio: 'Extravío',
    obsolescencia: 'Obsolescencia técnica',
    otro: 'Otro motivo',
};

const estadoConfig = {
    pendiente: { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', label: 'Pendiente', icon: Clock },
    aprobada: { color: 'bg-green-100 text-green-700 border-green-300', label: 'Aprobada', icon: CheckCircle },
    rechazada: { color: 'bg-red-100 text-red-700 border-red-300', label: 'Rechazada', icon: XCircle },
};

export default function BomberosValidacionBajasPage() {
    const navigate = useNavigate();
    const { tienePermiso, loading: permisosLoading } = useBomberosPermisos();
    const [loading, setLoading] = useState(true);
    const [solicitudes, setSolicitudes] = useState([]);
    const [activeTab, setActiveTab] = useState('pendiente');
    
    // Dialog states
    const [showDetalleDialog, setShowDetalleDialog] = useState(false);
    const [showValidarDialog, setShowValidarDialog] = useState(false);
    const [selectedSolicitud, setSelectedSolicitud] = useState(null);
    const [accionValidar, setAccionValidar] = useState(null);
    const [motivoRechazo, setMotivoRechazo] = useState('');
    const [validando, setValidando] = useState(false);

    useEffect(() => {
        loadSolicitudes();
    }, []);

    const loadSolicitudes = async () => {
        try {
            setLoading(true);
            const res = await bomberosSolicitudesBajaAPI.getAll();
            setSolicitudes(res.data);
        } catch (err) {
            console.error('Error loading solicitudes:', err);
            toast.error('Error al cargar solicitudes');
        } finally {
            setLoading(false);
        }
    };

    const filteredSolicitudes = solicitudes.filter(s => s.estado === activeTab);

    const handleOpenDetalle = (solicitud) => {
        setSelectedSolicitud(solicitud);
        setShowDetalleDialog(true);
    };

    const handleOpenValidar = (solicitud, accion) => {
        setSelectedSolicitud(solicitud);
        setAccionValidar(accion);
        setMotivoRechazo('');
        setShowValidarDialog(true);
    };

    const handleValidar = async () => {
        if (accionValidar === 'rechazar' && !motivoRechazo.trim()) {
            toast.error('Debe indicar el motivo del rechazo');
            return;
        }

        setValidando(true);
        try {
            await bomberosSolicitudesBajaAPI.validar(selectedSolicitud.id, {
                accion: accionValidar,
                motivo_rechazo: accionValidar === 'rechazar' ? motivoRechazo : null,
            });
            toast.success(
                accionValidar === 'aprobar' 
                    ? 'Solicitud aprobada. El EPP ha sido dado de baja.' 
                    : 'Solicitud rechazada. El EPP vuelve a estado disponible.'
            );
            setShowValidarDialog(false);
            loadSolicitudes();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al procesar la solicitud');
        } finally {
            setValidando(false);
        }
    };

    // Stats
    const stats = {
        pendientes: solicitudes.filter(s => s.estado === 'pendiente').length,
        aprobadas: solicitudes.filter(s => s.estado === 'aprobada').length,
        rechazadas: solicitudes.filter(s => s.estado === 'rechazada').length,
    };

    if (permisosLoading || loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (!tienePermiso('validar_documentos')) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
                <AlertTriangle className="h-16 w-16 text-yellow-500 mb-4" />
                <h2 className="text-xl font-semibold mb-2">Acceso Restringido</h2>
                <p className="text-muted-foreground text-center mb-4">
                    No tiene permisos para validar documentos de baja.
                </p>
                <Button onClick={() => navigate('/bomberos/dashboard')}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Volver al Dashboard
                </Button>
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 md:pt-20">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <FileCheck className="h-6 w-6" />
                            Validación de Bajas EPP
                        </h1>
                        <p className="text-muted-foreground">Aprobar o rechazar solicitudes de baja de equipamiento</p>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="border-yellow-200 bg-yellow-50/50">
                    <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-full bg-yellow-100">
                                <Clock className="h-5 w-5 text-yellow-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-yellow-700">{stats.pendientes}</p>
                                <p className="text-sm text-muted-foreground">Pendientes</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-green-200 bg-green-50/50">
                    <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-full bg-green-100">
                                <CheckCircle className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-green-700">{stats.aprobadas}</p>
                                <p className="text-sm text-muted-foreground">Aprobadas</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50/50">
                    <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-full bg-red-100">
                                <XCircle className="h-5 w-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-red-700">{stats.rechazadas}</p>
                                <p className="text-sm text-muted-foreground">Rechazadas</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="pendiente" className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Pendientes
                        {stats.pendientes > 0 && (
                            <Badge variant="secondary" className="ml-1">{stats.pendientes}</Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="aprobada" className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Aprobadas
                    </TabsTrigger>
                    <TabsTrigger value="rechazada" className="flex items-center gap-2">
                        <XCircle className="h-4 w-4" />
                        Rechazadas
                    </TabsTrigger>
                </TabsList>

                <TabsContent value={activeTab} className="mt-4">
                    <Card className="border-2">
                        <CardContent className="p-0">
                            {filteredSolicitudes.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                    <p>No hay solicitudes {activeTab === 'pendiente' ? 'pendientes' : activeTab === 'aprobada' ? 'aprobadas' : 'rechazadas'}</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>EPP</TableHead>
                                                <TableHead>Motivo</TableHead>
                                                <TableHead>Solicitante</TableHead>
                                                <TableHead>Fecha</TableHead>
                                                <TableHead>Estado</TableHead>
                                                <TableHead className="text-right">Acciones</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {filteredSolicitudes.map((sol) => {
                                                const config = estadoConfig[sol.estado];
                                                const IconEstado = config.icon;
                                                return (
                                                    <TableRow key={sol.id}>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <HardHat className="h-4 w-4 text-muted-foreground" />
                                                                <div>
                                                                    <p className="font-medium">{sol.material_codigo || 'N/A'}</p>
                                                                    <p className="text-xs text-muted-foreground">{sol.material_tipo || ''}</p>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="text-sm">{motivoLabels[sol.motivo] || sol.motivo}</span>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <User className="h-4 w-4 text-muted-foreground" />
                                                                <span className="text-sm">{sol.solicitante_nombre || 'N/A'}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                                                <span className="text-sm">
                                                                    {sol.created_at ? new Date(sol.created_at).toLocaleDateString('es-CL') : 'N/A'}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge className={config.color}>
                                                                <IconEstado className="h-3 w-3 mr-1" />
                                                                {config.label}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex justify-end gap-2">
                                                                <Button 
                                                                    variant="ghost" 
                                                                    size="sm"
                                                                    onClick={() => handleOpenDetalle(sol)}
                                                                >
                                                                    <Eye className="h-4 w-4" />
                                                                </Button>
                                                                {sol.estado === 'pendiente' && (
                                                                    <>
                                                                        <Button 
                                                                            variant="ghost" 
                                                                            size="sm"
                                                                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                                                            onClick={() => handleOpenValidar(sol, 'aprobar')}
                                                                        >
                                                                            <CheckCircle className="h-4 w-4" />
                                                                        </Button>
                                                                        <Button 
                                                                            variant="ghost" 
                                                                            size="sm"
                                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                            onClick={() => handleOpenValidar(sol, 'rechazar')}
                                                                        >
                                                                            <XCircle className="h-4 w-4" />
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Detalle Dialog */}
            <Dialog open={showDetalleDialog} onOpenChange={setShowDetalleDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Detalle de Solicitud
                        </DialogTitle>
                    </DialogHeader>
                    {selectedSolicitud && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs text-muted-foreground">Código EPP</Label>
                                    <p className="font-medium">{selectedSolicitud.material_codigo || 'N/A'}</p>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Tipo</Label>
                                    <p className="font-medium">{selectedSolicitud.material_tipo || 'N/A'}</p>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Motivo</Label>
                                    <p className="font-medium">{motivoLabels[selectedSolicitud.motivo] || selectedSolicitud.motivo}</p>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Estado</Label>
                                    <Badge className={estadoConfig[selectedSolicitud.estado].color}>
                                        {estadoConfig[selectedSolicitud.estado].label}
                                    </Badge>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Solicitante</Label>
                                    <p className="font-medium">{selectedSolicitud.solicitante_nombre || 'N/A'}</p>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Fecha Solicitud</Label>
                                    <p className="font-medium">
                                        {selectedSolicitud.created_at 
                                            ? new Date(selectedSolicitud.created_at).toLocaleString('es-CL')
                                            : 'N/A'}
                                    </p>
                                </div>
                            </div>

                            {selectedSolicitud.observaciones && (
                                <div>
                                    <Label className="text-xs text-muted-foreground">Observaciones</Label>
                                    <p className="text-sm mt-1 p-2 bg-muted rounded">{selectedSolicitud.observaciones}</p>
                                </div>
                            )}

                            {selectedSolicitud.documento_evidencia_url && (
                                <div>
                                    <Label className="text-xs text-muted-foreground">Documento de Evidencia</Label>
                                    <a 
                                        href={`${BACKEND_URL}${selectedSolicitud.documento_evidencia_url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 mt-1 p-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                                    >
                                        <FileText className="h-4 w-4" />
                                        Ver Acta de Baja
                                        <ExternalLink className="h-3 w-3 ml-auto" />
                                    </a>
                                </div>
                            )}

                            {selectedSolicitud.estado !== 'pendiente' && (
                                <div className="border-t pt-4 mt-4">
                                    <Label className="text-xs text-muted-foreground">Validado por</Label>
                                    <p className="font-medium">{selectedSolicitud.validador_nombre || 'N/A'}</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {selectedSolicitud.fecha_validacion 
                                            ? new Date(selectedSolicitud.fecha_validacion).toLocaleString('es-CL')
                                            : ''}
                                    </p>
                                    {selectedSolicitud.motivo_rechazo && (
                                        <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                                            <Label className="text-xs text-red-600">Motivo del rechazo</Label>
                                            <p className="text-sm text-red-700">{selectedSolicitud.motivo_rechazo}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDetalleDialog(false)}>
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Validar Dialog */}
            <Dialog open={showValidarDialog} onOpenChange={setShowValidarDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {accionValidar === 'aprobar' ? (
                                <>
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                    Aprobar Solicitud de Baja
                                </>
                            ) : (
                                <>
                                    <XCircle className="h-5 w-5 text-red-600" />
                                    Rechazar Solicitud de Baja
                                </>
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedSolicitud && (
                                <span>EPP: <strong>{selectedSolicitud.material_codigo}</strong></span>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {accionValidar === 'aprobar' ? (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <p className="text-sm text-green-800">
                                    <CheckCircle className="h-4 w-4 inline mr-2" />
                                    Al aprobar esta solicitud, el EPP será marcado como <strong>"Dado de Baja"</strong> permanentemente y no podrá ser asignado a ningún bombero.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <p className="text-sm text-red-800">
                                        <XCircle className="h-4 w-4 inline mr-2" />
                                        Al rechazar esta solicitud, el EPP volverá a estado <strong>"Disponible"</strong>.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label>Motivo del Rechazo *</Label>
                                    <Textarea
                                        value={motivoRechazo}
                                        onChange={(e) => setMotivoRechazo(e.target.value)}
                                        placeholder="Indique el motivo por el cual rechaza esta solicitud..."
                                        rows={3}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setShowValidarDialog(false)} disabled={validando}>
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handleValidar} 
                            disabled={validando || (accionValidar === 'rechazar' && !motivoRechazo.trim())}
                            className={accionValidar === 'aprobar' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                        >
                            {validando ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Procesando...
                                </>
                            ) : accionValidar === 'aprobar' ? (
                                <>
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Aprobar Baja
                                </>
                            ) : (
                                <>
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Rechazar
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </>
    );
}
